import {
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
  CognitoUserSession
} from 'amazon-cognito-identity-js';
import { useAuthContext, userPool } from 'contexts/AuthProvider';

const getSession: (
  user: CognitoUser
) => Promise<CognitoUserSession | null> = async user => {
  return new Promise(resolve =>
    user.getSession((err: any, session: CognitoUserSession) => {
      if (err || !session) {
        if (err) console.log('error get session: ', err);
        if (!session)
          console.log('error session is null in getsession', session);
        return resolve(undefined);
      }

      console.log('get session no error: ', session);
      return resolve(session);
    })
  );
};

const useAuth: {
  (): CognitoServiceType;
  authDetails: { username: string; password: string } | null;
} = () => {
  const { authUser, setAuthUser, setSession } = useAuthContext();

  const signup = async (
    username: string,
    email: string,
    password: string
  ): Promise<SignupResponseType> => {
    const attributeEmail = new CognitoUserAttribute({
      Name: 'email',
      Value: email
    });

    useAuth.authDetails = { username, password };

    const signupPromise = new Promise((resolve, reject) =>
      userPool.signUp(username, password, [attributeEmail], [], function(
        err,
        result
      ) {
        if (err) {
          return reject(err.message || JSON.stringify(err));
        }

        if (!result) {
          return reject('signup failed: no result');
        }

        const cognitoUser = result.user;
        setAuthUser(cognitoUser);
        return resolve();
      })
    );

    try {
      await signupPromise;
      return {};
    } catch (e) {
      return { error: e };
    }
  };

  const login = async (
    username: string,
    password: string
  ): Promise<LoginResponseType> => {
    const authData = new AuthenticationDetails({
      Username: username,
      Password: password
    });
    useAuth.authDetails = { username, password };
    const userData = {
      Username: username,
      Pool: userPool
    };
    const cognitoUser = new CognitoUser(userData);
    const authenticationPromise = new Promise((resolve, reject) =>
      cognitoUser.authenticateUser(authData, {
        onSuccess: () => resolve(),
        onFailure: error => reject(error)
      })
    );

    try {
      await authenticationPromise;
      const session = await getSession(cognitoUser);
      if (session) {
        setAuthUser(cognitoUser);
        setSession(session);
      }
      return { authUser: cognitoUser };
    } catch (e) {
      if (e.code === 'UserNotConfirmedException') {
        setAuthUser(cognitoUser);
      }
      return { error: e };
    }
  };

  const logout = async (): Promise<LogoutResponseType> => {
    const logoutPromise = new Promise((resolve, reject) => {
      try {
        if (authUser) {
          authUser.signOut();
          setAuthUser(null);
          setSession(null);
          return resolve();
        }

        setSession(null);
        setAuthUser(null);
        return reject('no user');
      } catch (e) {
        setSession(null);
        setAuthUser(null);
        return reject(e);
      }
    });

    try {
      await logoutPromise;
      return {};
    } catch (e) {
      return { error: e };
    }
  };

  const verify = async (code: string): Promise<VerifyResponseType> => {
    if (!authUser) return { error: 'no user' };

    const confirmationPromise = new Promise<VerifyResponseType>(
      (resolve, reject) => {
        if (!authUser) {
          return reject({ error: 'no user' });
        }
        authUser.confirmRegistration(code, true, function(err, result) {
          if (err) {
            return reject(err);
          }

          return resolve(result);
        });
      }
    );

    try {
      const { result } = await confirmationPromise;
      if (!useAuth.authDetails) {
        return { error: 'no auth details' };
      }
      await login(useAuth.authDetails.username, useAuth.authDetails.password);
      useAuth.authDetails = null;
      return { result };
    } catch (e) {
      useAuth.authDetails = null;
      return { error: e };
    }
  };

  const resendVerification = (): Promise<ResendVerificationResponseType> => {
    return new Promise<ResendVerificationResponseType>((resolve, reject) => {
      if (!authUser) {
        return reject({ error: 'No User' });
      }
      authUser.resendConfirmationCode((err, result) => {
        if (err) {
          return reject({ error: err });
        }
        return resolve({ result });
      });
    });
  };

  const cognitoServiceFunctions: CognitoServiceType = {
    signup,
    login,
    logout,
    verify,
    resendVerification
  };

  return cognitoServiceFunctions;
};

useAuth.authDetails = null;

export default useAuth;

/** types */
type SignupResponseType = { error?: string };

type LogoutResponseType = { error?: string };

type LoginResponseType = {
  error?: { message: string; code: string };
  authUser?: CognitoUser | null;
};

type VerifyResponseType = {
  error?: any;
  result?: any;
};

type ResendVerificationResponseType = {
  error?: Error | string;
  result?: 'SUCCESS';
};

type CognitoServiceType = {
  signup: (
    username: string,
    email: string,
    password: string
  ) => Promise<SignupResponseType>;
  login: (username: string, password: string) => Promise<LoginResponseType>;
  logout: () => Promise<LogoutResponseType>;
  verify: (code: string) => Promise<VerifyResponseType>;
  resendVerification: () => Promise<ResendVerificationResponseType>;
};
